import { BaseEntity } from "./base.model";

export interface Company extends BaseEntity {
  id: string;
  name: string;
  enabled: boolean;
  contactAddress?: string;
  phone?: string;
  email?: string;
  logo?: string;
  country?: string;
  timezone?: string;
  websiteUrl?: string;
  charts: ChartConfig[];
  filters?: FilterConfig[];
  area: string;
  region: string;
  storeType: string;
  stores?: SimpleCompanyStore[];
  showComplainsPage?: boolean;
}

export interface FilterConfig {
  name: string;
  label: string;
  options: { label: string; value: string }[];
}

export interface ChartConfig {
  key: string;
  label?: string;
  visible: boolean;
}

export interface SimpleCompanyStore {
  id: string;
  name: string;
  area: string;
  region: string;
  storeType: string;
}

export function getChartTitle(key: ChartEnum, company?: Company) {
  return company?.charts.find((a) => a.key === key)?.label || "";
}

export enum ChartEnum {
  "doughnut_1" = "doughnut_1",
  "doughnut_2" = "doughnut_2",
  "doughnut_3" = "doughnut_3",
  "doughnut_4" = "doughnut_4",
  "doughnut_5" = "doughnut_5",
  "doughnut_6" = "doughnut_6",
  "doughnut_7" = "doughnut_7",
  "doughnut_8" = "doughnut_8",
  "doughnut_9" = "doughnut_9",
  "doughnut_10" = "doughnut_10",
  "line_1" = "line_1",
  "line_2" = "line_2",
  "line_3" = "line_3",
  "bar_1" = "bar_1",
  "bar_2" = "bar_2",
  "bar_3" = "bar_3",
  "bar_4" = "bar_4",
  "bar_5" = "bar_5",
  "bar_6" = "bar_6",
  "bar_7" = "bar_7",
  "bar_8" = "bar_8",
  "bar_9" = "bar_9",
  "bar_10" = "bar_10",
  "line_bar_1" = "line_bar_1",
  "half_doughnut_1" = "half_doughnut_1",
  "half_doughnut_2" = "half_doughnut_2",
  "square_count_1" = "square_count_1",
  "square_count_2" = "square_count_2",
  "horizontal_bar_1" = "horizontal_bar_1",
  "horizontal_bar_2" = "horizontal_bar_2",
  "horizontal_bar_3" = "horizontal_bar_3",
  "compare_table_1" = "compare_table_1",
  "compare_table_2" = "compare_table_2",
  "comment_list_1" = "comment_list_1",
  "ta_explore" = "ta_explore",
  "brand" = "brand",
  "comments_1" = "comments_1",
  "comments_2" = "comments_2",
  "cards_1" = "cards_1",
  "cards_2" = "cards_2",
}

export const timezoneOptions = [
  { label: "-12:00", value: -12 },
  { label: "-11:00", value: -11 },
  { label: "-10:00", value: -10 },
  { label: "-09:00", value: -9 },
  { label: "-08:00", value: -8 },
  { label: "-07:00", value: -7 },
  { label: "-06:00", value: -6 },
  { label: "-05:00", value: -5 },
  { label: "-04:00", value: -4 },
  { label: "-03:00", value: -3 },
  { label: "-02:00", value: -2 },
  { label: "-01:00", value: -1 },
  { label: "+00:00", value: 0 },
  { label: "+01:00", value: 1 },
  { label: "+02:00", value: 2 },
  { label: "+03:00", value: 3 },
  { label: "+04:00", value: 4 },
  { label: "+05:00", value: 5 },
  { label: "+06:00", value: 6 },
  { label: "+07:00", value: 7 },
  { label: "+08:00", value: 8 },
  { label: "+09:00", value: 9 },
  { label: "+10:00", value: 10 },
  { label: "+11:00", value: 11 },
  { label: "+12:00", value: 12 },
];

export type ChartLayoutConfig = {
  companyId: string;
  pages: Page[];
};

export type Page = {
  title: string;
  url: string;
  charts: Chart[];
};

export type Chart = {
  width: number;
  type: string;
  label: string;
  data: ChartData[];
  doughnut?: Doughnut;
  bar?: Bar;
  line?: Line;
  mixed?: Mixed;
  groupBy?: ChartGroupByEnum;
  showInHierarchy?: boolean;
  storeLabelTypes?: string[];
};

export type Doughnut = {
  center: DoughnutCenter;
};

export type DoughnutCenter = {
  prefix?: string;
  expression: string;
  suffix?: string;
  decimalPlaces?: number;
};

export type Bar = {
  direction: BarDirectionEnum;
  dataSource: ChartDataSourceEnum;
  tickesFormat?: ChartTickesFormatEnum;
};

export type Line = {
  dataSource: ChartDataSourceEnum;
  tickesFormat?: ChartTickesFormatEnum;
};

export type Mixed = {
  tickesFormat?: ChartTickesFormatEnum;
};

export type ChartData = {
  label: string;
  expression: string;
  decimalPlaces?: number;
  color: string;
  mixedType?: ChartTypeEnum.Line | ChartTypeEnum.Bar;
  tableDateRange?: TableDateRangeEnum;
  tableDataFormation?: TableDataFormationEnum;
};

export enum ChartTypeEnum {
  Doughnut = "doughnut",
  Bar = "bar",
  Line = "line",
  Mixed = "mixed",
  Table = "table",
}

export enum BarDirectionEnum {
  Horizontal = "horizontal",
  Vertical = "vertical",
}

export enum ChartDataSourceEnum {
  Main = "main",
  DateRange = "dates",
}

export enum ChartTickesFormatEnum {
  Decimal = "decimal",
  Percent = "percent",
  Currency = "currency",
}

export enum ChartGroupByEnum {
  Store = "stores",
  Region = "region",
  Area = "area",
}

export enum TableDateRangeEnum {
  All = "all",
  LastMonth = "lastMonth",
  LastYear = "lastYear",
  ThisMonth = "thisMonth",
  ThisYear = "thisYear",
  LastLastMonth = "lastLastMonth",
  LastLastYear = "lastLastYear",
}

export enum TableDataFormationEnum {
  Default = "default",
  ShowInBidirectional = "showInBidirectional",
  Percentage = "percentage",
}
